import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Link,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { MergedHoliday } from "../types";
import { getStateColor, getStateTextColor } from "../constants/stateColors";

interface HolidayDetailsModalProps {
  open: boolean;
  holiday: MergedHoliday | null;
  onClose: () => void;
}

export const HolidayDetailsModal: React.FC<HolidayDetailsModalProps> = ({
  open,
  holiday,
  onClose,
}) => {
  if (!holiday) return null;

  // Format date from dateString (YYYYMMDD format)
  const formatDate = (dateString: string) => {
    try {
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6));
      const day = parseInt(dateString.substring(6, 8));
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-AU", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{holiday.holidayName}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Holiday Type & States */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              APPLIES TO
            </Typography>
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {holiday.type === "federal" ? (
                <Chip
                  label="Federal"
                  variant="filled"
                  sx={{
                    bgcolor: "#9313b3ff",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                />
              ) : (
                holiday.states.map((state) => (
                  <Chip
                    key={state}
                    label={state}
                    variant="filled"
                    sx={{
                      bgcolor: getStateColor(state),
                      color: getStateTextColor(state),
                      fontWeight: 500,
                    }}
                  />
                ))
              )}
            </Box>
          </Box>

          {/* Date */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              DATE
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {formatDate(holiday.dateString)}
            </Typography>
          </Box>

          {/* Information if available */}
          {holiday.information && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                INFORMATION
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {holiday.information}
              </Typography>
            </Box>
          )}

          {/* regionOnly if availabkle */}
          {holiday.regionalOnly && holiday.regionalInfo && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                REGIONAL HOLIDAY
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {holiday.regionalInfo}
              </Typography>
            </Box>
          )}  

          {/* special conditions if available */}
          {holiday.specialConditions && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                SPECIAL CONDITIONS
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {holiday.specialConditions}
              </Typography>
            </Box>
          )}  

          {/* Sources if available */}
          {holiday.sources && holiday.sources.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                SOURCES ({holiday.sources.length})
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {holiday.sources.map((source, idx) => {
                  const stateColor = source.state ? getStateColor(source.state) : "#e0e0e0";
                  const lightBgColor = lighten(stateColor, 0.95);

                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 1,
                        mb: 1,
                        bgcolor: lightBgColor,
                        color: "#000",
                        borderRadius: 1,
                        fontSize: "0.875rem",
                        borderLeft: `3px solid ${stateColor}`,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 500, display: "block" }}>
                        {source.holidayName} -{" "}
                        {source.state && (
                          <Typography
                            variant="caption"
                            sx={{ display: "inline", mt: 0.25, opacity: 0.7 }}
                          >
                            {source.state}
                          </Typography>
                        )}
                      </Typography>

                      {source.information && (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                          {source.information}
                        </Typography>
                      )}
                      {source.infoLink && (
                        <Link
                          href={source.infoLink}
                          target="_blank"
                          rel="noopener"
                          variant="caption"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {source.infoLink}
                        </Link>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Part Day Info if available */}
          {holiday.partDay && holiday.partDayInfo && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                PART DAY
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {holiday.partDayInfo}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary"
        onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
